import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * SearchContext - Extended context for multi-search and refine state
 * 
 * Provides:
 * - Multi-search tab management (max 5 concurrent searches)
 * - Shared refine state for filters, weights, list size
 * - Background search execution support
 * 
 * ZIA Standard: Max 5 searches to prevent memory issues
 * KAI Standard: Multi-chained agent support
 */
const SearchContext = createContext(null);

// Maximum concurrent searches (ZIA recommendation)
const MAX_SEARCHES = 5;

// Generate unique search ID
const generateSearchId = () => `search-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export function SearchProvider({ children }) {
  // Multi-search state
  const [searches, setSearches] = useState([]);
  const [activeSearchId, setActiveSearchId] = useState(null);
  
  // Legacy refine state (backward compatibility)
  const [refineState, setRefineState] = useState({
    filters: [],
    outputFields: [],
    rankingWeights: {},
    listSize: 25,
    listMode: 'short', // 'short' | 'long'
  });

  // Get active search object
  const activeSearch = searches.find(s => s.id === activeSearchId) || null;

  // Create new search
  const createSearch = useCallback((query, filters = [], mode = 'people', options = {}) => {
    const id = generateSearchId();
    const newSearch = {
      id,
      query: query || '',
      filters,
      mode,
      status: 'pending', // 'pending' | 'running' | 'complete' | 'error'
      results: [],
      resultsCount: 0,
      createdAt: Date.now(),
      name: options.name || null,
      error: null,
    };
    
    setSearches(prev => {
      // Enforce max searches limit - remove oldest completed if at limit
      let updated = [...prev];
      if (updated.length >= MAX_SEARCHES) {
        const oldestComplete = updated.find(s => s.status === 'complete');
        if (oldestComplete) {
          updated = updated.filter(s => s.id !== oldestComplete.id);
        } else {
          // Can't add more - all are running
          console.warn('Max concurrent searches reached');
          return prev;
        }
      }
      return [...updated, newSearch];
    });
    
    // Auto-activate new search
    setActiveSearchId(id);
    
    return id;
  }, []);

  // Update search status and results
  const updateSearch = useCallback((searchId, updates) => {
    setSearches(prev => prev.map(s => 
      s.id === searchId ? { ...s, ...updates } : s
    ));
  }, []);

  // Mark search as running
  const startSearch = useCallback((searchId) => {
    updateSearch(searchId, { status: 'running' });
  }, [updateSearch]);

  // Mark search as complete with results
  const completeSearch = useCallback((searchId, results) => {
    updateSearch(searchId, { 
      status: 'complete', 
      results, 
      resultsCount: results?.length || 0 
    });
  }, [updateSearch]);

  // Mark search as error
  const errorSearch = useCallback((searchId, error) => {
    updateSearch(searchId, { 
      status: 'error', 
      error: error?.message || 'Search failed' 
    });
  }, [updateSearch]);

  // Close/remove search
  const closeSearch = useCallback((searchId) => {
    setSearches(prev => {
      const updated = prev.filter(s => s.id !== searchId);
      // If closing active search, switch to another
      if (searchId === activeSearchId && updated.length > 0) {
        setActiveSearchId(updated[updated.length - 1].id);
      } else if (updated.length === 0) {
        setActiveSearchId(null);
      }
      return updated;
    });
  }, [activeSearchId]);

  // Switch active search
  const selectSearch = useCallback((searchId) => {
    setActiveSearchId(searchId);
  }, []);

  // Legacy refine state methods (backward compatibility)
  const updateFilters = useCallback((filters, outputFields) => {
    setRefineState(prev => ({
      ...prev,
      filters: filters || prev.filters,
      outputFields: outputFields || prev.outputFields,
    }));
    
    // Also update active search filters
    if (activeSearchId) {
      updateSearch(activeSearchId, { filters });
    }
  }, [activeSearchId, updateSearch]);

  const updateRankingWeights = useCallback((weights) => {
    setRefineState(prev => ({
      ...prev,
      rankingWeights: weights || prev.rankingWeights,
    }));
  }, []);

  const updateListSize = useCallback((size, mode) => {
    setRefineState(prev => ({
      ...prev,
      listSize: size !== undefined ? size : prev.listSize,
      listMode: mode || prev.listMode,
    }));
  }, []);

  const resetRefineState = useCallback(() => {
    setRefineState({
      filters: [],
      outputFields: [],
      rankingWeights: {},
      listSize: 25,
      listMode: 'short',
    });
  }, []);

  return (
    <SearchContext.Provider
      value={{
        // Multi-search state
        searches,
        activeSearchId,
        activeSearch,
        createSearch,
        updateSearch,
        startSearch,
        completeSearch,
        errorSearch,
        closeSearch,
        selectSearch,
        maxSearches: MAX_SEARCHES,
        
        // Legacy refine state (backward compatibility)
        refineState,
        updateFilters,
        updateRankingWeights,
        updateListSize,
        resetRefineState,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearchContext() {
  const context = useContext(SearchContext);
  if (!context) {
    // Return default values if context not available (graceful degradation)
    return {
      // Multi-search state (defaults)
      searches: [],
      activeSearchId: null,
      activeSearch: null,
      createSearch: () => null,
      updateSearch: () => {},
      startSearch: () => {},
      completeSearch: () => {},
      errorSearch: () => {},
      closeSearch: () => {},
      selectSearch: () => {},
      maxSearches: MAX_SEARCHES,
      
      // Legacy refine state
      refineState: {
        filters: [],
        outputFields: [],
        rankingWeights: {},
        listSize: 25,
        listMode: 'short',
      },
      updateFilters: () => {},
      updateRankingWeights: () => {},
      updateListSize: () => {},
      resetRefineState: () => {},
    };
  }
  return context;
}
